import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AudiLogo from '../components/AudiLogo';

const { FiAward, FiUsers, FiClock, FiShield, FiHeart, FiTrendingUp, FiMapPin, FiChevronRight, FiZap } = FiIcons;

const About = () => {
  const stats = [
    { icon: FiUsers, number: '15,000+', label: 'Satisfied Audi Owners' },
    { icon: FiClock, number: '15+', label: 'Years Serving Halifax' },
    { icon: FiAward, number: '25+', label: 'Audi-Certified Technicians' },
    { icon: FiShield, number: '100%', label: 'Genuine Audi Parts' }
  ];

  const values = [
    {
      icon: FiHeart,
      title: 'Customer Excellence',
      description: 'Every interaction reflects our commitment to providing an exceptional Audi ownership experience that exceeds expectations.'
    },
    {
      icon: FiShield,
      title: 'Audi Standards',
      description: 'We maintain the highest standards in everything we do, from vehicle preparation to service delivery, upholding Audi\'s reputation for excellence.'
    },
    {
      icon: FiTrendingUp,
      title: 'Innovation Leadership',
      description: 'We embrace Audi\'s progressive philosophy, staying at the forefront of automotive technology and customer service innovation.'
    }
  ];

  const milestones = [
    {
      year: '2008',
      title: 'Halifax Audi Founded',
      description: 'Halifax Audi opened as Nova Scotia\'s premier Audi dealership, bringing German engineering excellence to the Maritime provinces.'
    },
    {
      year: '2012',
      title: 'Service Center Expansion',
      description: 'Expanded our service facilities with state-of-the-art diagnostic equipment and added specialized Audi technician training programs.'
    },
    {
      year: '2015',
      title: 'Audi Certified Pre-Owned Launch',
      description: 'Introduced our comprehensive Audi Certified Pre-Owned program, setting new standards for quality pre-owned luxury vehicles.'
    },
    {
      year: '2018',
      title: 'Showroom Modernization',
      description: 'Completed a major renovation reflecting Audi\'s contemporary design philosophy and enhanced customer experience areas.'
    },
    {
      year: '2021',
      title: 'e-tron Experience Center',
      description: 'Opened a dedicated electric vehicle experience center featuring Audi\'s complete e-tron lineup and charging solutions.'
    },
    {
      year: '2023',
      title: 'quattro Winter Experience',
      description: 'Launched specialized quattro AWD demonstration programs showcasing Audi\'s legendary all-wheel drive in Maritime conditions.'
    }
  ];

  const audiHeritage = [
    {
      year: '1969',
      milestone: 'quattro AWD Pioneered',
      description: 'Audi revolutionized automotive traction with the first quattro all-wheel drive system'
    },
    {
      year: '1980s',
      milestone: 'Rally Dominance',
      description: 'quattro technology dominated international rallying, proving its superiority'
    },
    {
      year: '2010s',
      milestone: 'LED Innovation',
      description: 'Audi became the first to use LED daytime running lights as a design signature'
    },
    {
      year: '2018',
      milestone: 'e-tron Electric',
      description: 'Launched the e-tron, bringing luxury electric mobility to the premium segment'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1614200179396-2bdb77ebf81b?w=1920&h=600&fit=crop&crop=center" 
            alt="Halifax Audi dealership" 
            className="w-full h-full object-cover opacity-60" 
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light mb-6 uppercase tracking-tight">
              About Halifax Audi
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto font-light">
              For over 15 years, we've been Nova Scotia's gateway to the world of Audi - 
              where progress through technology meets Maritime hospitality.
            </p>
            <div className="flex items-center justify-center space-x-2 mt-4 text-gray-300">
              <SafeIcon icon={FiMapPin} className="w-5 h-5" />
              <span>6077 Columbus St, Halifax, NS B3K 0E9</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-red-600 p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={stat.icon} className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  {stat.number}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Audi Story Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Our Audi Heritage
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Halifax Audi was founded in 2008 with a vision to bring the authentic Audi experience 
                  to Nova Scotia. As an authorized Audi dealer, we represent more than just vehicles - 
                  we embody Audi's philosophy of "Vorsprung durch Technik" (progress through technology).
                </p>
                <p>
                  Our showroom on Columbus Street has become Halifax's destination for those who appreciate 
                  German engineering excellence, cutting-edge technology, and the legendary quattro 
                  all-wheel drive system that's perfect for Maritime conditions.
                </p>
                <p>
                  From the sporty A4 sedan to the luxurious Q7 SUV, from the innovative e-tron electric 
                  models to the high-performance RS lineup, we offer the complete Audi experience with 
                  expert knowledge and genuine Maritime hospitality.
                </p>
                <p>
                  Our commitment extends beyond sales to comprehensive service, genuine parts, and 
                  building lasting relationships with Audi enthusiasts throughout the Atlantic provinces.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1627725475306-a8666e8255b8?w=800" 
                alt="Halifax Audi showroom" 
                className="w-full h-auto shadow-lg" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Audi Brand Heritage */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Audi Innovation Timeline
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Key innovations that have shaped Audi's reputation for technological leadership 
              and engineering excellence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {audiHeritage.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 border-l-4 border-red-600"
              >
                <div className="text-red-600 font-bold text-lg mb-2">{item.year}</div>
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  {item.milestone}
                </h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Halifax Audi Journey
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Key milestones in our commitment to bringing the Audi experience to Halifax 
              and the Maritime provinces.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-gray-200"></div>

            {/* Timeline Events */}
            <div className="relative space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} items-center`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                    <div className="bg-white p-6 border border-gray-200 shadow-sm">
                      <span className="text-red-600 font-medium">{milestone.year}</span>
                      <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white">
                    {milestone.year.substring(2)}
                  </div>
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Our Audi Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide our commitment to the Audi brand and our customers 
              throughout the Maritime provinces.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 border border-gray-200 text-center hover:shadow-lg transition-shadow"
              >
                <div className="bg-red-600 p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-light text-gray-900 mb-4 uppercase tracking-tight">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* quattro Technology Showcase */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="flex items-center space-x-4 mb-6">
                <SafeIcon icon={FiZap} className="w-12 h-12 text-red-600" />
                <AudiLogo className="h-16 w-auto" />
              </div>
              <h2 className="text-3xl md:text-4xl font-light text-white mb-6 uppercase tracking-tight">
                quattro: Born from Innovation
              </h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  In 1980, Audi revolutionized automotive engineering with the introduction of quattro 
                  all-wheel drive. What began as a rally racing innovation has become the gold standard 
                  for all-weather traction and performance.
                </p>
                <p>
                  Today's quattro system intelligently distributes power between all four wheels, 
                  providing superior grip in Halifax winters, confidence on wet Maritime roads, 
                  and enhanced performance in all driving conditions.
                </p>
                <p>
                  From the Audi Quattro rally car that dominated international motorsport to today's 
                  sophisticated quattro ultra technology, this legendary system continues to define 
                  what it means to drive with confidence.
                </p>
              </div>

              <div className="mt-8">
                <Link 
                  to="/models" 
                  className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
                >
                  <span>Explore quattro Models</span>
                  <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <img 
                src="https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800" 
                alt="Audi quattro technology" 
                className="w-full h-auto shadow-lg" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4 uppercase tracking-tight">
              Experience Halifax Audi
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto font-light">
              Visit our Columbus Street showroom to experience the complete world of Audi - 
              from innovative technology to legendary quattro performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="bg-white text-red-600 px-8 py-3 font-medium hover:bg-gray-100 transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Visit Our Showroom
              </Link>
              <Link 
                to="/inventory" 
                className="border-2 border-white text-white px-8 py-3 font-medium hover:bg-white hover:text-red-600 transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Browse Audi Inventory
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;